import { Eye } from 'lucide-react';
import styled from 'styled-components';

import { IconButton } from '../../../../components/atoms/icon-button/icon-button';
import { UsuarioInterno } from '../../../../types/usuario';
import { formatDate, formatDateTime, summarizePermissions } from './format';
import { UserStatusBadge } from './user-status-badge';

interface Props {
  items: UsuarioInterno[];
  onOpenDetail: (id: string) => void;
}

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  background: ${({ theme }) => theme.colors.bgSurface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  overflow: hidden;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textPrimary};

  thead th {
    text-align: left;
    padding: 12px 16px;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: ${({ theme }) => theme.colors.textMuted};
    background: ${({ theme }) => theme.colors.bgMuted};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    font-weight: 600;
  }

  tbody td {
    padding: 14px 16px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  tbody tr:hover {
    background: ${({ theme }) => theme.colors.bgMuted};
  }

  th:last-child,
  td:last-child {
    text-align: right;
  }
`;

const Cell = styled.div`
  display: flex;
  flex-direction: column;
  line-height: 1.3;

  strong {
    font-weight: 600;
  }

  small {
    color: ${({ theme }) => theme.colors.textMuted};
    font-size: 12.5px;
  }
`;

const SrOnly = styled.caption`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

export const UsuariosTable = ({ items, onOpenDetail }: Props) => (
  <Table>
    <SrOnly>Lista de usuários internos</SrOnly>
    <thead>
      <tr>
        <th scope="col">Nome</th>
        <th scope="col">Status</th>
        <th scope="col">Permissões</th>
        <th scope="col">Último login</th>
        <th scope="col">Criado em</th>
        <th scope="col">Ações</th>
      </tr>
    </thead>
    <tbody>
      {items.map((user) => (
        <tr key={user.id} data-testid={`user-row-${user.id}`}>
          <td>
            <Cell>
              <strong>{user.name}</strong>
              <small>{user.email}</small>
            </Cell>
          </td>
          <td>
            <UserStatusBadge active={user.active} />
          </td>
          <td>
            <Cell>
              <span>{summarizePermissions(user.permissions)}</span>
              <small>{user.permissions.length} total</small>
            </Cell>
          </td>
          <td>{formatDateTime(user.lastLoginAt)}</td>
          <td>{formatDate(user.createdAt)}</td>
          <td>
            <IconButton
              label={`Ver detalhes do usuário ${user.name}`}
              onClick={() => onOpenDetail(user.id)}
            >
              <Eye size={16} />
            </IconButton>
          </td>
        </tr>
      ))}
    </tbody>
  </Table>
);
